import { useQueryClient } from "@tanstack/react-query";

/**
 * Hook centralizado para invalidar queries relacionadas a pedidos.
 *
 * REGRAS DE USO:
 * - invalidateAll()          → operações globais: criar pedido, cancelar pedido
 * - invalidateKanban()       → ações do kanban/BKO: mover item, travar/liberar BKO
 * - invalidateItemPedido()   → editar item específico: editar célula na planilha
 * - invalidateClienteAuxiliar() → dados auxiliares do cliente: gestor, cessionário
 *
 * NÃO usar invalidateAll() para operações pontuais — viola a regra de sobrecarga do Supabase.
 */
export function useInvalidateAllPedidoQueries() {
  const queryClient = useQueryClient();

  /**
   * SOMENTE para: criar pedido novo, cancelar pedido, operações que afetam tudo.
   * ~32 queries — use com parcimônia.
   */
  const invalidateAll = () => {
    // Queries de pedidos diretos e totais
    queryClient.invalidateQueries({ queryKey: ['pedido_totais'] });
    queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-detail'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-360'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-item'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'] });
    queryClient.invalidateQueries({ queryKey: ['pedidos-cliente-visao360'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-cliente-tab'] });
    queryClient.invalidateQueries({ queryKey: ['empresa-pedidos'] });
    
    // Queries de visualizações
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['planilha'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['planilha-pedidos'] });
    queryClient.invalidateQueries({ queryKey: ['planilha-vistas-salvas'] });
    
    // Queries de carteira e clientes
    queryClient.invalidateQueries({ queryKey: ['carteira-clientes'] });
    queryClient.invalidateQueries({ queryKey: ['clientes'] });
    queryClient.invalidateQueries({ queryKey: ['cliente-detail'] });
    
    // Queries de leads e carteira
    queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
    queryClient.invalidateQueries({ queryKey: ['meus-leads'] });
    queryClient.invalidateQueries({ queryKey: ['lead-historico'] });
    
    // Queries de dashboard e métricas
    queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    queryClient.invalidateQueries({ queryKey: ['venda-diaria'] });
    queryClient.invalidateQueries({ queryKey: ['ranking'] });
    queryClient.invalidateQueries({ queryKey: ['metas'] });
    
    // Queries de qualidade e cobertura
    queryClient.invalidateQueries({ queryKey: ['qualidade'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-cobertura-fibra'] });
    
    // Queries de timeline e histórico
    queryClient.invalidateQueries({ queryKey: ['timeline'] });
    queryClient.invalidateQueries({ queryKey: ['historico-status'] });
    queryClient.invalidateQueries({ queryKey: ['movimento'] });
    
    // Queries de anexos e mensagens
    queryClient.invalidateQueries({ queryKey: ['pedido-anexos'] });
    queryClient.invalidateQueries({ queryKey: ['mensagens'] });
    
    // Status e transições
    queryClient.invalidateQueries({ queryKey: ['status-catalogo'] });
    queryClient.invalidateQueries({ queryKey: ['status-transicoes'] });
  };

  /**
   * Para: ações do kanban/BKO, lock/unlock, triagem, mover item.
   * ~6 queries — substitui invalidateAll() neste contexto.
   */
  const invalidateKanban = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['kanban-optimized'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['pedido_totais'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-itens-completo'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['historico-status'] });
  };

  /**
   * Para: editar célula na planilha (consultor, status, data).
   * ~5 queries — substitui invalidateAll() neste contexto.
   */
  const invalidateItemPedido = () => {
    queryClient.invalidateQueries({ queryKey: ['kanban'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['planilha-itens'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    queryClient.invalidateQueries({ queryKey: ['pedido-detail'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['historico-status'] });
  };

  /**
   * Para: salvar gestor, salvar cessionário — dados auxiliares do cliente.
   * ~3 queries — substitui invalidateAll() neste contexto.
   */
  const invalidateClienteAuxiliar = () => {
    queryClient.invalidateQueries({ queryKey: ['cliente-cessionarios'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['pedido-detail'], exact: false });
    queryClient.invalidateQueries({ queryKey: ['pedido-360'], exact: false });
  };

  return { invalidateAll, invalidateKanban, invalidateItemPedido, invalidateClienteAuxiliar };
}
